import React, {useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef} from "react"

import {FieldInlineEditableProps} from "../../../../../render/inline-editable"
import TimeReport from "../../../../../../model2/time-report/time-report"
import FieldTimeReported_InlineValue from "./inline-edit/value-not-empty"
import FieldTimeSpent from "../../time-spent"
import {ITimeSpentPopoverParent} from "../../components/popover/popover-parent"

export type FieldTimeReported_EditableRef = {
    showPopover: () => void
}

type Props = FieldInlineEditableProps & {
    field   : FieldTimeSpent
}

function buildTimeReport(model: any, field: FieldTimeSpent): TimeReport {
    const jsonData = model?.toJSON() || {}
    return new TimeReport(_.assign({}, jsonData, {
        'reportedTimeMinutes'   : jsonData[field.fieldId] ?? jsonData['reportedTimeMinutes'],
        'startMoment'           : jsonData[field.startMomentFieldId],
        'endMoment'             : jsonData[field.endMomentFieldId],
    }))
}

// const debug = "FieldTimeReported_Editable"
const debug = false

const FieldTimeReported_Editable = forwardRef<FieldTimeReported_EditableRef, Props>((props, ref) => {

    const containerRef = useRef<HTMLDivElement>(null)

    const [timeReport, setTimeReport] = useState<TimeReport>(() => buildTimeReport(props.model, props.field))
    const [showPopover, setShowPopover] = useState(false)

    useEffect(() => {
        setTimeReport(buildTimeReport(props.model, props.field))
    }, [
        props.model.reportedTimeMinutes,
        props.model.startMoment,
        props.model.endMoment,
    ])

    const notifyChange = (timeReport: TimeReport) => {
        props.onChange?.(timeReport.reportedTimeMinutes, timeReport.startMoment, timeReport.endMoment)
    }

    const onShowPopover = useCallback((e?: any) => {
        e?.stopPropagation()
        setShowPopover(true)
    }, [])

    useImperativeHandle(ref, () => ({
        showPopover: onShowPopover,
    }), [onShowPopover])

    const hidePopover = useCallback((e?: any) => {
        e?.stopPropagation()
        setShowPopover(false)
    }, [])

    const onValueChange = (value: any, commitChange: boolean = false) => {
        debug && console.log(debug, 'onValueChange', value, commitChange, _.isEqual(timeReport.reportedTimeMinutes, value))
        if (_.isEqual(timeReport.reportedTimeMinutes, value)) {
            return
        }

        const next = new TimeReport(timeReport.toJSON())
        next.reportedTimeMinutes = value
        setTimeReport(next)
        notifyChange(next)
        if (commitChange) {
            setShowPopover(false)
        }
    }

    const onIsBillableChange = (isBillable: boolean) => {
        debug && console.log(debug, 'onIsBillableChange', isBillable)
    }

    const onTimeChange = (startMoment?: Moment, endMoment?: Moment) => {
        debug && console.log(debug, 'onTimeChange', startMoment, endMoment)

        const next = new TimeReport(timeReport.toJSON())
        if (!_.isUndefined(startMoment)) {
            next.startMoment = startMoment
        }
        if (!_.isUndefined(endMoment)) {
            next.endMoment = endMoment
        }
        setTimeReport(next)
        notifyChange(next)
    }

    // The following block creates a ref object, `parentRef`, to serve as a holder for a set of callback functions
    // that will be accessed by child components (specifically the PopoverMenu) via the `parent` prop.
    // This design allows the popover to call methods like `onValueChange`, `hidePopover`, etc., on its parent.
    // Each function is assigned to a property on `parentRef.current`, ensuring the latest handler (from props or state)
    // is always accessible to the PopoverMenu when it is shown.
    const parentRef = useRef<ITimeSpentPopoverParent>({} as ITimeSpentPopoverParent)
    parentRef.current.onValueChange         = onValueChange
    parentRef.current.hidePopover           = hidePopover
    parentRef.current.onIsBillableChange    = onIsBillableChange
    parentRef.current.onTimeChange          = onTimeChange

    const classes = Classes('h-100p w-100p', props.className, {
        'show-popover' : showPopover,
    })

    debug && console.log(debug, 'render', timeReport)

    return (
        <FieldTimeReported_InlineValue
            ref         = {containerRef}
            className   = {classes}
            timeReport  = {timeReport}
            onClick     = {onShowPopover}
            field       = {props.field}
        >
            {showPopover &&
                <props.field.components.PopoverMenu
                    field           = {props.field}
                    options         = {props.options}
                    parent          = {parentRef.current}
                    model           = {timeReport}
                    hideIsBillable  = {true}
                    getTargetFunc   = {() => containerRef.current}
                    onChange        = {props.onChange}
                />}
        </FieldTimeReported_InlineValue>
    )
})

export default React.memo(FieldTimeReported_Editable, (prev, next) =>
    prev.field                      === next.field      &&
    prev.onChange                   === next.onChange   &&
    prev.className                  === next.className  &&
    prev.options                    === next.options    &&
    prev.model?.reportedTimeMinutes === next.model?.reportedTimeMinutes &&
    prev.model?.startMoment         === next.model?.startMoment         &&
    prev.model?.endMoment           === next.model?.endMoment
)
